import { KeyConfigurations } from '@enums/KeyConfigurations';
import { useAppSelector } from '@hooks/useAppSelector';
import { MotionButton, MotionText } from '@shared/atoms';
import React from 'react';
import styles from './index.module.scss';

interface AnswerOptionsProps {
  answers: { id: number; optionText: string; score: number }[];
  onSelectAnswer: (answerId: number, answerScore: number) => void;
  className?: string;
  qId: number;
  'aria-labelledby'?: string;
  /** When provided, selection state is controlled locally instead of via questionnaire Redux state. */
  selectedAnswerId?: number | null;
}

export const AnswerOptions: React.FC<AnswerOptionsProps> = ({
  answers,
  onSelectAnswer,
  className,
  qId,
  'aria-labelledby': ariaLabelledBy,
  selectedAnswerId,
}) => {
  const {
    questionnaire,
    answers: stateAnswers,
    currentQuestionIndex,
  } = useAppSelector((state) => state.questionnaireV2 || {});

  const questions = questionnaire?.questionnairesV2Questions;
  const useControlledSelection = selectedAnswerId !== undefined;

  return (
    <div className={className} role="radiogroup" aria-labelledby={ariaLabelledBy}>
      {answers.map((answer, index) => {
        let isActive = false;

        if (useControlledSelection) {
          isActive = selectedAnswerId === answer.id;
        } else if (questions) {
          if (questions[currentQuestionIndex]?.id === Number(qId)) {
            isActive = Object.values(stateAnswers).some(
              (ans) => ans.answerId === answer.id && ans.questionId === qId,
            );
          }
        }

        const optionId = `answer-${qId}-${answer.id}`;
        const optionLetter = String.fromCharCode(65 + index);

        return (
          <MotionButton
            key={answer.id}
            whileHover={{ opacity: 0.8 }}
            onClick={() => onSelectAnswer(answer.id, answer.score)}
            className={isActive ? styles.activeAnswer : ''}
            role="radio"
            aria-checked={isActive}
            id={optionId}
            aria-label={`Option ${optionLetter}: ${answer.optionText}`}
            onKeyDown={(e: { key: string; preventDefault: () => void }) => {
              if (e.key === KeyConfigurations.ENTER || e.key === KeyConfigurations.SPACE) {
                e.preventDefault();
                onSelectAnswer(answer.id, answer.score);
              }
            }}
            tabIndex={0}
          >
            {/* A, B, C, D characters for options */}
            {/* <span aria-hidden="true">{String.fromCharCode(65 + index)}</span> */}
            {/* &nbsp;&nbsp;&nbsp;&nbsp; */}
            <MotionText as="span" className={styles.optionText} text={answer.optionText} />
          </MotionButton>
        );
      })}

      {answers.length === 0 && (
        <MotionText
          as="p"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          text=" There are no answers available for this question."
          role="status"
        />
      )}
    </div>
  );
};

AnswerOptions.defaultProps = {
  className: styles.answersContainer,
  'aria-labelledby': undefined,
  selectedAnswerId: undefined,
};
